import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import Input from '../components/elements/Input';
import { useAuth } from '../contexts/AuthContext';
import { createReceiptWithBalance, updateReceiptWithBalance, getReceiptById, getFileOpeningBalanceInfo } from '../services/receipts';
import toast from 'react-hot-toast';
import { pdf } from '@react-pdf/renderer';
import ReceiptPdf from '../components/pdf/ReceiptPdf';
import { getFileOpeningById } from '../services/fileOpenings';
import { renderAddress } from '../utils/helper';

const ReceiptCreate = () => {
    const { id, receiptId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        amount: '',
        payment_method: 'cash',
        payment_received: true,
        reference_number: '',
        comments: ''
    });
    const [loading, setLoading] = useState(true);
    const [balanceInfo, setBalanceInfo] = useState({
        total_invoices: 0,
        total_received: 0,
        balance_amount: 0
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                // Get current balance information
                const balanceData = await getFileOpeningBalanceInfo(id);
                console.log('Balance Data:', balanceData);
                setBalanceInfo(balanceData);

                let initialFormData = {
                    date: new Date().toISOString().split('T')[0],
                    amount: '',
                    payment_method: 'cash',
                    payment_received: true,
                    reference_number: '',
                    comments: ''
                };

                // If editing existing receipt, fetch receipt data
                if (receiptId) {
                    const receiptData = await getReceiptById(receiptId);
                    initialFormData = {
                        ...initialFormData,
                        ...receiptData,
                        date: receiptData.date ? receiptData.date.split('T')[0] : initialFormData.date,
                        amount: receiptData.amount?.toString() || ''
                    };
                }

                setFormData(initialFormData);

            } catch (error) {
                toast.error(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id, receiptId]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const calculateNewBalance = () => {
        const paymentAmount = parseFloat(formData.amount || 0);
        const currentBalance = balanceInfo.balance_amount;

        if (formData.payment_received) {
            return currentBalance - paymentAmount;
        }
        return currentBalance;
    };

    const getPaymentMethodIcon = (method) => {
        const icons = {
            cash: '💰',
            card: '💳',
            bank: '🏦',
            other: '📄'
        };
        return icons[method] || '📄';
    };

    const downloadPdf = async (receiptDate) => {
        try {
            const blob = await pdf(<ReceiptPdf data={receiptDate} />).toBlob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `receipt-${receiptDate.id || 'new'}-${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error generating PDF:', error);
            toast.error('Failed to generate PDF');
        }
    };

    const getPrintData = (data, fileData) => {
        return {
            ...data,
            client_name: fileData.title + " " + fileData.forename + " " + fileData.surname,
            address: `${renderAddress(fileData)}`,
            phone: fileData.phone || '',
            mobile: fileData.mobile || '',
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();

        const dataToSubmit = {
            ...formData,
            fid: id,
            user_id: user?.id,
            amount: parseFloat(formData.amount) || 0
        };

        if (!user) {
            toast.error('You must be logged in to create a receipt');
            return;
        }

        try {
            // Always fetch file opening data first
            const fileData = await getFileOpeningById(id);
            if (receiptId) {
                const data = await updateReceiptWithBalance(receiptId, dataToSubmit);
                await downloadPdf(getPrintData(data, fileData));
                toast.success('Receipt updated successfully!');
            } else {
                const data = await createReceiptWithBalance(dataToSubmit);
                await downloadPdf(getPrintData(data, fileData));
                toast.success('Receipt created successfully!');
            }
            navigate(`/file-opening/${id}/receipts`);
        } catch (error) {
            toast.error(error.message);
        }
    };

    if (loading) {
        return <DashboardLayout pageTitle="Loading..."><p className='dark:text-white'>Loading...</p></DashboardLayout>;
    }

    const newBalance = calculateNewBalance();

    return (
        <DashboardLayout pageTitle={receiptId ? 'Edit Receipt' : 'Create New Receipt'}>
            <div className="max-w-4xl mx-auto">
                <div>
                    <button onClick={() => navigate(-1)} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md shadow ml-4 mb-4">
                        Back
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-4 sm:p-8 mb-10">
                    <div className="flex justify-between items-center mb-6">
                        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 dark:text-white">
                            {receiptId ? 'Edit Receipt' : 'Create New Receipt'}
                        </h1>
                    </div>

                    {/* Balance Summary */}
                    <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <h3 className="font-semibold mb-2 text-gray-800 dark:text-white">Balance Summary</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="text-gray-600 dark:text-gray-400">Total Invoices:</span>
                                <div className="font-semibold dark:text-gray-50">£{balanceInfo.total_invoices.toFixed(2)}</div>
                            </div>
                            <div>
                                <span className="text-gray-600 dark:text-gray-400">Total Received:</span>
                                <div className="font-semibold dark:text-gray-50">£{balanceInfo.total_received.toFixed(2)}</div>
                            </div>
                            <div>
                                <span className="text-gray-600 dark:text-gray-400">Current Balance:</span>
                                <div className={`font-semibold ${balanceInfo.balance_amount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                    £{balanceInfo.balance_amount.toFixed(2)}
                                </div>
                            </div>
                            <div>
                                <span className="text-gray-600 dark:text-gray-400">New Balance:</span>
                                <div className={`font-semibold ${newBalance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                    £{newBalance.toFixed(2)}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <Input
                            label="Date"
                            type="date"
                            name="date"
                            value={formData.date}
                            onChange={handleChange}
                            required
                        />

                        <Input
                            label="Payment Amount (£)"
                            type="number"
                            name="amount"
                            value={formData.amount}
                            onChange={handleChange}
                            step="0.01"
                            min="0"
                            required
                        />

                        {/* Payment Method - Checkboxes */}
                        <div>
                            <label className="block font-semibold mb-2 text-gray-800 dark:text-white">
                                Payment Method
                            </label>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                {['cash', 'card', 'bank', 'other'].map((method) => (
                                    <label key={method} className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                                        <input
                                            type="radio"
                                            name="payment_method"
                                            value={method}
                                            checked={formData.payment_method === method}
                                            onChange={handleChange}
                                            className="hidden"
                                        />
                                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${formData.payment_method === method
                                            ? 'border-blue-500 bg-blue-500'
                                            : 'border-gray-300 dark:border-gray-600'
                                            }`}>
                                            {formData.payment_method === method && (
                                                <div className="w-2 h-2 rounded-full bg-white"></div>
                                            )}
                                        </div>
                                        <span className="flex items-center space-x-1">
                                            <span>{getPaymentMethodIcon(method)}</span>
                                            <span className="capitalize dark:text-gray-50">{method}</span>
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Payment Received - Radio Buttons */}
                        <div>
                            <label className="block font-semibold mb-2 text-gray-800 dark:text-white">
                                Payment Received
                            </label>
                            <div className="flex space-x-4">
                                <label className="flex items-center space-x-2">
                                    <input
                                        type="radio"
                                        name="payment_received"
                                        value={true}
                                        checked={formData.payment_received === true}
                                        onChange={() => setFormData(prev => ({ ...prev, payment_received: true }))}
                                        className="w-4 h-4 text-blue-600"
                                    />
                                    <span className="dark:text-gray-50 font-medium">Yes</span>
                                </label>
                                <label className="flex items-center space-x-2">
                                    <input
                                        type="radio"
                                        name="payment_received"
                                        value={false}
                                        checked={formData.payment_received === false}
                                        onChange={() => setFormData(prev => ({ ...prev, payment_received: false }))}
                                        className="w-4 h-4 text-blue-600"
                                    />
                                    <span className="dark:text-gray-50 font-medium">No</span>
                                </label>
                            </div>
                        </div>

                        <Input
                            label="Reference Number"
                            type="text"
                            name="reference_number"
                            value={formData.reference_number}
                            onChange={handleChange}
                            placeholder="Enter reference number..."
                        />

                        <div>
                            <label className="block font-semibold mb-1 text-gray-800 dark:text-white">
                                Comments
                            </label>
                            <textarea
                                name="comments"
                                className="w-full border rounded-md p-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                                rows="4"
                                placeholder="Enter any comments/notes..."
                                value={formData.comments}
                                onChange={handleChange}
                            ></textarea>
                        </div>
                    </div>

                    <div className="flex justify-end mt-8">
                        <button
                            type="submit"
                            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-md shadow text-sm sm:text-base"
                        >
                            {receiptId ? 'Update Receipt' : 'Create Receipt'}
                        </button>
                    </div>
                </form>
            </div>
        </DashboardLayout>
    );
};

export default ReceiptCreate;